import { BookOpen, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Child, ReadingEntry } from '../lib/types';
import { useParent } from './ParentContext';

const shortDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

/** The child's reading log (#189): what they logged in the Student Portal, for the parent to sign off. */
export function ReadingLog({ child }: { child: Child }) {
  const { toast } = useParent();
  const [entries, setEntries] = useState<ReadingEntry[] | null>(null);
  const load = useCallback(() => {
    api.get<ReadingEntry[]>(`/api/children/${child.id}/reading`).then(setEntries).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [child.id, toast]);
  useEffect(load, [load]);

  async function sign(e: ReadingEntry, signed: boolean) {
    try {
      const updated = await api.post<ReadingEntry>(`/api/reading/${e.id}/sign-off`, { signed });
      setEntries((list) => list?.map((x) => (x.id === e.id ? updated : x)) ?? null);
    } catch (err) {
      toast({ text: (err as Error).message, tone: 'alert' });
    }
  }

  async function remove(e: ReadingEntry) {
    if (!confirm(`Delete “${e.title}” on ${shortDate(e.date)}?`)) return;
    try {
      await api.del(`/api/reading/${e.id}`);
      setEntries((list) => list?.filter((x) => x.id !== e.id) ?? null);
    } catch (err) {
      toast({ text: (err as Error).message, tone: 'alert' });
    }
  }

  if (!entries) return <p className="muted">Loading…</p>;
  const pages = entries.reduce((n, e) => n + e.pages, 0);
  const minutes = entries.reduce((n, e) => n + e.minutes, 0);
  const waiting = entries.filter((e) => !e.signedOff).length;

  return (
    <section className="card stack">
      <h2 className="with-icon">
        <BookOpen size={18} /> {child.name}’s reading log
      </h2>
      <p className="muted small">
        {child.name} adds entries in the Student Portal, and you can add them here too (they’re signed off already). Tick{' '}
        <strong>Signed off</strong> once you’ve checked one — after that {child.name} can’t change it.
        {waiting > 0 && ` ${waiting} waiting for your sign-off.`}
      </p>
      {entries.length === 0 ? (
        <p className="muted">Nothing logged yet.</p>
      ) : (
        <div className="report-table-wrap">
          <table className="report-table reading-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Book</th>
                <th>Pages</th>
                <th>Minutes</th>
                <th>Signed off</th>
                <th aria-label="Delete" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td>{shortDate(e.date)}</td>
                  <td className="reading-title">
                    {e.title}
                    {e.addedBy && <span className="muted small block">Added by {e.addedBy}</span>}
                  </td>
                  <td>{e.pages}</td>
                  <td>{e.minutes}</td>
                  <td>
                    <label className="check compact" title={e.signedOff && e.signedOffBy ? `Signed off by ${e.signedOffBy}` : undefined}>
                      <input type="checkbox" checked={e.signedOff} onChange={(ev) => sign(e, ev.target.checked)} aria-label={`Sign off ${e.title} on ${e.date}`} />
                    </label>
                  </td>
                  <td>
                    <button className="icon-btn" onClick={() => remove(e)} aria-label={`Delete ${e.title} on ${e.date}`}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={2}>Total</th>
                <th>{pages}</th>
                <th>{minutes}</th>
                <th />
                <th />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <AddReading childId={child.id} onAdded={(e) => setEntries((list) => [e, ...(list ?? [])].sort((a, b) => b.date.localeCompare(a.date)))} />
    </section>
  );
}

/** The parent adds a reading entry (#339), e.g. a book read aloud together. */
function AddReading({ childId, onAdded }: { childId: string; onAdded: (e: ReadingEntry) => void }) {
  const { toast } = useParent();
  const today = new Date();
  const [date, setDate] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
  const [title, setTitle] = useState('');
  const [pages, setPages] = useState('');
  const [minutes, setMinutes] = useState('');
  const [busy, setBusy] = useState(false);
  async function add() {
    setBusy(true);
    try {
      onAdded(await api.post<ReadingEntry>(`/api/children/${childId}/reading`, { date, title: title.trim(), pages: Number(pages) || 0, minutes: Number(minutes) || 0 }));
      setTitle('');
      setPages('');
      setMinutes('');
      toast({ text: 'Reading entry added.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack sub-section">
      <h3 className="with-icon">
        <Plus size={16} /> Add an entry
      </h3>
      <div className="field-row reading-add">
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="grow">
          Book
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="e.g. Charlotte’s Web" />
        </label>
        <label>
          Pages
          <input type="number" min={0} value={pages} onChange={(e) => setPages(e.target.value)} />
        </label>
        <label>
          Minutes
          <input type="number" min={0} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
        </label>
      </div>
      <button className="btn primary" disabled={busy || !title.trim() || !date} onClick={add}>
        Add entry
      </button>
    </div>
  );
}
